import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CampaignDisplayStatus } from "@signalist/shared-types";

import { CampaignStatus } from "@/enum/campaign-status.enum";

import { useGetAnalyticsOverviewMutation } from "../../../redux/analytics/analytics.api";
import { useUpdateCampaignStatusMutation } from "../../../redux/list/list.api";

import { AnalyticsFilterBar } from "../components/AnalyticsFilterBar";
import { AnalyticsEmptyState } from "../components/AnalyticsEmptyState";
import { AnalyticsSkeleton } from "../components/AnalyticsSkeleton";
import { OverviewHighlights } from "../components/OverviewHighlights";
import { OverviewTable } from "../components/OverviewTable";
import { AnalyticsSelection } from "../hooks/useAnalyticsSelection";
import { useOpenCampaignDeepDive } from "../hooks/useOpenCampaignDeepDive";
import { OverviewCampaignRow, OverviewListRow } from "../schema/overview";

export interface OverviewTabProps {
	selection: AnalyticsSelection;
}

/** Statuses whose pill offers a pause/resume toggle. */
const TOGGLEABLE = new Set([
	CampaignDisplayStatus.ACTIVE,
	CampaignDisplayStatus.PAUSED,
	CampaignDisplayStatus.READY,
]);

/**
 * The portfolio view: every selected list with its campaigns, the numbers side by side,
 * and a click-through into the Campaign Deep Dive.
 */
export const OverviewTab: FunctionComponent<OverviewTabProps> = ({ selection }) => {
	const { t } = useTranslation();
	const [getOverview, { data, isLoading }] = useGetAnalyticsOverviewMutation();
	const [updateCampaignStatus] = useUpdateCampaignStatusMutation();
	const [togglingCampaignId, setTogglingCampaignId] = useState<string | null>(null);

	const { payload } = selection;

	useEffect(() => {
		getOverview(payload).unwrap();
	}, [payload]);

	/**
	 * Clicking a campaign switches to the Campaign Deep Dive scoped to it — the point of
	 * the overview is to find the campaign worth looking at, then look at it.
	 */
	const openCampaignDeepDive = useOpenCampaignDeepDive(selection.selectListsWithParams);

	const handleCampaignClick = useCallback(
		(row: OverviewListRow, campaign: OverviewCampaignRow) =>
			openCampaignDeepDive({
				listId: row.listId,
				listName: row.listName,
				campaignId: campaign.campaignId,
				campaignName: campaign.name,
			}),
		[openCampaignDeepDive]
	);

	const handleCampaignToggle = useCallback(
		async (row: OverviewListRow, campaign: OverviewCampaignRow) => {
			if (!campaign.status || !TOGGLEABLE.has(campaign.status)) {
				return;
			}

			setTogglingCampaignId(campaign.campaignId);

			try {
				await updateCampaignStatus({
					id: row.listId,
					campaignUuid: campaign.campaignId,
					status:
						campaign.status === CampaignDisplayStatus.ACTIVE
							? CampaignStatus.PAUSED
							: CampaignStatus.RUNNING,
				}).unwrap();

				// Re-read the report so the pill and the numbers agree again.
				await getOverview(payload).unwrap();
			} finally {
				setTogglingCampaignId(null);
			}
		},
		[updateCampaignStatus, getOverview, payload]
	);

	return (
		<>
			<AnalyticsFilterBar
				campaigns={selection.campaigns}
				dateFromToValue={selection.dateFromToValue}
				listOptions={selection.listOptions}
				selectedLists={selection.selectedLists}
				senderUserIds={selection.senderUserIds}
				onCampaignToggle={selection.onCampaignToggle}
				onDateChange={selection.onDateChange}
				onListContentFilterToggle={selection.onListContentFilterToggle}
				onListSelect={selection.onListSelect}
				onSenderChange={selection.onSenderChange}
			/>

			{isLoading ? (
				<AnalyticsSkeleton />
			) : data && data.rows.length > 0 ? (
				<div className="flex flex-col w-full mt-4">
					<OverviewHighlights highlights={data.highlights} />
					<OverviewTable
						rows={data.rows}
						togglingCampaignId={togglingCampaignId}
						onCampaignClick={handleCampaignClick}
						onCampaignToggle={handleCampaignToggle}
					/>
					<p className="text-xs text-text-meta mt-3">{t("analytics.overview.footnote")}</p>
				</div>
			) : (
				<AnalyticsEmptyState />
			)}
		</>
	);
};
